/**
 * Sets up global debug commands that can be used in the browser console
 * to enable/disable debugging without changing code or restarting the app.
 * 
 * Usage from browser console:
 * - debug.enable()         - Enable all debug logs
 * - debug.disable()        - Disable all debug logs
 * - debug.component('Home')- Enable debug for a specific component
 * - debug.components(['Home', 'Chatbot']) - Enable debug for multiple components 
 * - debug.off('Chatbot')   - Disable debug for a specific component
 * - debug.status()         - Show current debug status
 */

import logger from './logger';
import { 
  enableDebug, 
  disableDebug, 
  enableComponentDebug, 
  disableComponentDebug, 
  getDebugConfig, 
  listDebugComponents 
} from './debugConfig';

const setupConsoleDebug = () => {
  // Safe check for environment
  const isProduction = typeof window !== 'undefined' && 
                      window.process && 
                      window.process.env && 
                      window.process.env.NODE_ENV === 'production';
  
  // Skip in production
  if (isProduction) return;
  
  // Create global debug object with helpful methods
  window.debug = {
    enable: () => {
      enableDebug();
      console.log('🔍 Debug enabled');
      return getDebugConfig();
    },
    
    disable: () => {
      disableDebug();
      console.log('🔍 Debug disabled');
      return getDebugConfig();
    },
    
    component: (component) => {
      enableComponentDebug(component);
      console.log(`🔍 Debug enabled for component: ${component}`);
      return getDebugConfig();
    },
    
    components: (componentArray) => {
      enableComponentDebug(componentArray);
      console.log(`🔍 Debug enabled for components: ${componentArray.join(', ')}`);
      return getDebugConfig();
    },
    
    off: (component) => {
      disableComponentDebug(component);
      console.log(`🔍 Debug disabled for component: ${component}`);
      return getDebugConfig();
    },
    
    status: () => {
      const config = getDebugConfig();
      console.log(`🔍 Debug status: ${config.enabled ? 'ENABLED' : 'DISABLED'}`);
      console.log(`🔍 Active components: ${config.components.length ? config.components.join(', ') : 'none'}`);
      return config;
    },
    
    // Direct access to logger for other operations
    logger
  };
  
  console.log(`
    🔧 Debug commands available:
    • debug.enable()        - Enable all debug logs
    • debug.disable()       - Disable all debug logs
    • debug.component('X')  - Enable debug for component X
    • debug.components(['X', 'Y']) - Enable debug for specific components
    • debug.off('X')        - Disable debug for component X
    • debug.status()        - Show current debug status
  `);
};

export default setupConsoleDebug; 